import { TOGGLE_LINK_COMMAND, $isLinkNode, $isAutoLinkNode, $createLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  $getNodeByKey,
  CLICK_COMMAND,
  SELECTION_CHANGE_COMMAND,
  LexicalEditor,
  NodeKey,
} from 'lexical';
import { ExternalLink, Unlink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSelectedNode } from './utils/getSelectedNode';
import { sanitizeUrl } from './utils/sanitizeUrl';
import { getUniformLinkInfo } from './utils/selectionUtils';
import { IconButton } from '@/Domain/components/Shared/Button/Button';

function getSelectionRect(): DOMRect | null {
  if (typeof window === 'undefined') return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  const rects = range.getClientRects?.();
  if (rects && rects.length > 0) {
    // Use the last rect so multi-line selections anchor at the bottom line
    return rects[rects.length - 1];
  }
  return range.getBoundingClientRect();
}

function getLinkElementRect(editor: LexicalEditor, key: NodeKey | null): DOMRect | null {
  if (!key) return null;
  try {
    const elem = editor.getElementByKey(key);
    if (elem) return elem.getBoundingClientRect();
  } catch {
    // Ignore errors when getting element
  }
  return null;
}

// Use the new uniform selection checker instead of the old logic
// This ensures we only show the popover when the selection is consistently within one link

export const SHOW_LINK_EDITOR_COMMAND = createCommand<void>('SHOW_LINK_EDITOR');

export default function FloatingLinkEditorPlugin({
  container,
}: {
  container?: HTMLElement | null;
}) {
  const [editor] = useLexicalComposerContext();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  const interactingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const currentLinkKeyRef = useRef<string | null>(null);
  const lastRectRef = useRef<{ midX: number; bottom: number } | null>(null);
  const isEditingRef = useRef(false);
  const justOpenedRef = useRef(false);
  const fallbackUrlRef = useRef<string>('');

  const stopAll = (e: React.MouseEvent<HTMLElement>) => {
    // Only stop propagation; do NOT stopImmediatePropagation so button onClick still fires
    e.stopPropagation();
  };

  const clampToViewport = (midX: number, width: number) => {
    if (typeof window === 'undefined') return midX;
    const padding = 8;
    const half = width / 2;
    const max = window.innerWidth - padding - half;
    const min = padding + half;
    return Math.min(Math.max(midX, min), max);
  };

  const clampY = (bottom: number, height: number) => {
    if (typeof window === 'undefined') return bottom;
    const padding = 8;
    const desired = bottom + 6;
    const maxTop = window.innerHeight - height - padding;
    return Math.min(desired, maxTop);
  };

  const holdOpenUntilRef = useRef<number>(0);

  const update = () => {
    // While typing/editing inside the popover, do not update/close.
    if (isEditingRef.current) {
      return;
    }
    if (Date.now() < holdOpenUntilRef.current) {
      // Only respect hold-open while focus is inside the popover
      const active =
        typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
      const isInside = anchorRef.current && active && anchorRef.current.contains(active);
      if (isInside) {
        return;
      }
    }
    const sel = $getSelection();
    const info = getUniformLinkInfo(sel as any);
    if (!info.isUniform || !info.linkNode) {
      // If user is interacting or focus is within the popover, do not close.
      if (interactingRef.current) return;
      const active =
        typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
      if (anchorRef.current && active && anchorRef.current.contains(active)) return;
      setOpen(false);
      setUrl('');
      currentLinkKeyRef.current = null;
      fallbackUrlRef.current = '';
      return;
    }

    try {
      const current = info.url ?? '';
      const active =
        typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
      // Allow initial prime even if focused inside right after open
      const isInside = anchorRef.current && active && anchorRef.current.contains(active);
      const canWrite = !isInside || justOpenedRef.current;
      if (canWrite) {
        // Avoid clearing fallback right after opening if selection hasn't stabilized yet
        if (current && current.length > 0) {
          setUrl(current);
        } else if (justOpenedRef.current && fallbackUrlRef.current) {
          setUrl(fallbackUrlRef.current);
        }
      }
      currentLinkKeyRef.current = info.key;
    } catch {
      // Ignore errors when getting link info
    }
    // Measure position in rAF to avoid layout timing issues in Safari
    requestAnimationFrame(() => {
      let rect = getLinkElementRect(editor, currentLinkKeyRef.current);
      if (!rect) rect = getSelectionRect();
      if (rect) {
        const midX = rect.left + rect.width / 2;
        const approxWidth = anchorRef.current?.offsetWidth ?? 280;
        const approxHeight = anchorRef.current?.offsetHeight ?? 44;
        const left = clampToViewport(midX, approxWidth);
        const top = clampY(rect.bottom, approxHeight);
        lastRectRef.current = { midX, bottom: rect.bottom };
        setCoords({ left, top });
        setOpen(true);
      } else {
        setOpen(false);
      }
    });
  };

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          editor.getEditorState().read(update);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // Update on clicks inside the editor to avoid needing a second click
  useEffect(
    () =>
      editor.registerCommand(
        CLICK_COMMAND,
        (event: MouseEvent) => {
          // Try to position using the clicked anchor element if present
          const anchorElem = (event.target as HTMLElement | null)?.closest('a');
          if (anchorElem) {
            const rect = anchorElem.getBoundingClientRect();
            const midX = rect.left + rect.width / 2;
            const approxWidth = anchorRef.current?.offsetWidth ?? 280;
            const approxHeight = anchorRef.current?.offsetHeight ?? 44;
            const left = clampToViewport(midX, approxWidth);
            const top = clampY(rect.bottom, approxHeight);
            lastRectRef.current = { midX, bottom: rect.bottom };
            // Capture href from clicked anchor as a fallback until selection stabilizes
            try {
              const a = anchorElem as HTMLAnchorElement;
              fallbackUrlRef.current = a.getAttribute('href') || a.href || fallbackUrlRef.current;
              if (fallbackUrlRef.current) {
                setUrl(fallbackUrlRef.current);
              }
            } catch {
              // Ignore errors when getting href
            }
            setCoords({ left, top });
            justOpenedRef.current = true;
            setOpen(true);
          }
          // On Safari, selection/DOM mapping can update after click propagation.
          // Defer to next frame to read a stable rect and refresh URL/key.
          requestAnimationFrame(() => {
            editor.getEditorState().read(update);
          });
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // Open on explicit command (from toolbar)
  useEffect(
    () =>
      editor.registerCommand(
        SHOW_LINK_EDITOR_COMMAND,
        () => {
          editor.getEditorState().read(() => {
            const info = getUniformLinkInfo($getSelection() as any);
            setUrl(info.url ?? '');
            currentLinkKeyRef.current = info.key;
            requestAnimationFrame(() => {
              let rect = getLinkElementRect(editor, currentLinkKeyRef.current);
              if (!rect) rect = getSelectionRect();
              if (rect) {
                const midX = rect.left + rect.width / 2;
                const approxWidth = anchorRef.current?.offsetWidth ?? 280;
                const approxHeight = anchorRef.current?.offsetHeight ?? 44;
                const left = clampToViewport(midX, approxWidth);
                const top = clampY(rect.bottom, approxHeight);
                lastRectRef.current = { midX, bottom: rect.bottom };
                setCoords({ left, top });
                justOpenedRef.current = true;
                setOpen(true);
              }
            });
          });
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  useEffect(() => {
    const onScroll = () => editor.getEditorState().read(update);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    const root = editor.getRootElement();
    if (root) root.addEventListener('scroll', onScroll, { capture: true, passive: true });
    // Do not close on global pointerdown to avoid Safari blink when opening.
    const onPointerUp = () => {
      // end of interaction, allow updates to close the popover if needed
      interactingRef.current = false;
    };
    window.addEventListener('pointerup', onPointerUp, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      if (root) root.removeEventListener('scroll', onScroll, { capture: true } as any);
      window.removeEventListener('pointerup', onPointerUp, true);
    };
  }, [editor]);

  // When opening, read current link, prime the input, focus, and re-clamp position
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        // On Safari, selection/DOM mapping can update after click propagation.
        // Use a second rAF to ensure selection is stable before reading.
        requestAnimationFrame(() => {
          // Prime value from editor state now that the input is mounted
          editor.getEditorState().read(() => {
            const info = getUniformLinkInfo($getSelection() as any);
            const prime = info.url && info.url.length > 0 ? info.url : fallbackUrlRef.current || '';
            setUrl(prime);
            currentLinkKeyRef.current = info.key;
          });
          // Recompute position with measured size to avoid cropping
          const width = anchorRef.current?.offsetWidth ?? 0;
          const height = anchorRef.current?.offsetHeight ?? 0;
          if (width && lastRectRef.current) {
            const left = clampToViewport(lastRectRef.current.midX, width);
            const top = clampY(lastRectRef.current.bottom, height || 44);
            setCoords({ left, top });
          }
          // Clear the just-opened write allowance and fallback after focus settles
          setTimeout(() => {
            justOpenedRef.current = false;
            fallbackUrlRef.current = '';
          }, 0);
        });
      });
    }
  }, [open, editor]);

  // removed duplicated effects

  // SSR guard - return null if not in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  const body = typeof document !== 'undefined' ? (container ?? document.body) : null;

  const content =
    !open || !coords ? null : (
      <div
        ref={anchorRef}
        className="z-10 fixed -translate-x-1/2 rounded-xl border border-border bg-card shadow-lg text-sm pointer-events-auto"
        style={{ left: coords.left, top: coords.top }}
        role="dialog"
        aria-modal="false"
        tabIndex={-1}
        onPointerDownCapture={() => {
          interactingRef.current = true;
          holdOpenUntilRef.current = Date.now() + 1500;
        }}
        onMouseDownCapture={() => {
          interactingRef.current = true;
          holdOpenUntilRef.current = Date.now() + 1500;
        }}
      >
        <div className="flex items-stretch p-1 gap-1">
          <input
            className="border border-border rounded px-2 py-1 w-64 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Paste or type a link"
            value={url}
            onChange={(e) => {
              // Keep local state in sync while editing
              const v = (e.target as HTMLInputElement).value;
              setUrl(v);
            }}
            ref={inputRef}
            aria-label="Link URL"
            aria-describedby="link-editor-help"
            onFocus={() => {
              interactingRef.current = true;
              isEditingRef.current = true;
              holdOpenUntilRef.current = Date.now() + 1500;
            }}
            onPointerDownCapture={(e) => {
              interactingRef.current = true;
              holdOpenUntilRef.current = Date.now() + 1500;
              e.stopPropagation();
              const ne: any = e.nativeEvent;
              if (ne && typeof ne.stopImmediatePropagation === 'function')
                ne.stopImmediatePropagation();
            }}
            onMouseDownCapture={(e) => {
              interactingRef.current = true;
              holdOpenUntilRef.current = Date.now() + 1500;
              e.stopPropagation();
              const ne: any = e.nativeEvent;
              if (ne && typeof ne.stopImmediatePropagation === 'function')
                ne.stopImmediatePropagation();
            }}
            onClick={() => {
              inputRef.current?.focus({ preventScroll: true });
              try {
                inputRef.current?.select();
              } catch {
                // Ignore errors when selecting input
              }
            }}
            onKeyDownCapture={(e) => {
              e.stopPropagation();
              const ne: any = e.nativeEvent;
              if (ne && typeof ne.stopImmediatePropagation === 'function')
                ne.stopImmediatePropagation();
            }}
            onInputCapture={(e) => {
              e.stopPropagation();
              const ne: any = e.nativeEvent;
              if (ne && typeof ne.stopImmediatePropagation === 'function')
                ne.stopImmediatePropagation();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const sanitized = sanitizeUrl(url ?? '');

                if (!sanitized) {
                  // Empty or invalid URL - remove link
                  editor.focus();
                  setTimeout(() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null), 0);
                  isEditingRef.current = false;
                  return;
                }

                // Valid URL - apply with AutoLink normalization
                editor.update(() => {
                  if (currentLinkKeyRef.current) {
                    const node = $getNodeByKey(currentLinkKeyRef.current);
                    if ($isAutoLinkNode(node)) {
                      const replaced = $createLinkNode(sanitized, {
                        rel: node.__rel,
                        target: node.__target,
                        title: node.__title,
                      });
                      node.replace(replaced, true);
                    } else if ($isLinkNode(node)) {
                      node.setURL(sanitized);
                    }
                    return;
                  }

                  // No link yet - wrap selection
                  editor.focus();
                  setTimeout(() => {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitized);

                    // If the selection is inside an AutoLink after toggling, convert it
                    editor.update(() => {
                      const sel = $getSelection();
                      if ($isRangeSelection(sel)) {
                        const parent = getSelectedNode(sel).getParent();
                        if ($isAutoLinkNode(parent)) {
                          const linkNode = $createLinkNode(parent.getURL(), {
                            rel: parent.__rel,
                            target: parent.__target,
                            title: parent.__title,
                          });
                          parent.replace(linkNode, true);
                        }
                      }
                    });
                  }, 0);
                });

                isEditingRef.current = false;
              } else if (e.key === 'Escape') {
                e.preventDefault();
                inputRef.current?.blur();
              }
            }}
            onBlur={(e) => {
              const relatedTarget = e.relatedTarget as HTMLElement | null;
              if (anchorRef.current && relatedTarget && anchorRef.current.contains(relatedTarget)) {
                return;
              }

              const sanitized = sanitizeUrl(url ?? '');

              if (!sanitized) {
                // Empty or invalid URL - remove link
                editor.focus();
                setTimeout(() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null), 0);
                setOpen(false);
                isEditingRef.current = false;
                fallbackUrlRef.current = '';
                return;
              }

              // Valid URL - apply with AutoLink normalization
              editor.update(() => {
                if (currentLinkKeyRef.current) {
                  const node = $getNodeByKey(currentLinkKeyRef.current);
                  if ($isAutoLinkNode(node)) {
                    const replaced = $createLinkNode(sanitized, {
                      rel: node.__rel,
                      target: node.__target,
                      title: node.__title,
                    });
                    node.replace(replaced, true);
                  } else if ($isLinkNode(node)) {
                    node.setURL(sanitized);
                  }
                  return;
                }

                // No link yet - wrap selection
                editor.focus();
                setTimeout(() => {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitized);

                  // If the selection is inside an AutoLink after toggling, convert it
                  editor.update(() => {
                    const sel = $getSelection();
                    if ($isRangeSelection(sel)) {
                      const parent = getSelectedNode(sel).getParent();
                      if ($isAutoLinkNode(parent)) {
                        const linkNode = $createLinkNode(parent.getURL(), {
                          rel: parent.__rel,
                          target: parent.__target,
                          title: parent.__title,
                        });
                        parent.replace(linkNode, true);
                      }
                    }
                  });
                }, 0);
              });

              isEditingRef.current = false;
              // Always close on outside blur
              setOpen(false);
              fallbackUrlRef.current = '';
            }}
          />
          <IconButton
            size="sm"
            variant="secondary"
            aria-label="Open link in new tab"
            type="button"
            onPointerDownCapture={(e) => {
              interactingRef.current = true;
              stopAll(e);
            }}
            onClick={() => {
              const value = (url ?? '').trim();
              if (value) window.open(value, '_blank', 'noopener,noreferrer');
            }}
          >
            <ExternalLink />
          </IconButton>
          <IconButton
            size="sm"
            variant="secondary"
            aria-label="Remove link"
            type="button"
            onPointerDownCapture={(e) => {
              interactingRef.current = true;
              stopAll(e);
            }}
            onClick={() => {
              editor.focus();
              setTimeout(() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null), 0);
              setOpen(false);
              setUrl('');
              currentLinkKeyRef.current = null;
              fallbackUrlRef.current = '';
            }}
          >
            <Unlink />
          </IconButton>
        </div>
        <div id="link-editor-help" className="sr-only">
          Enter a URL and press Enter to apply, or Escape to cancel.
        </div>
      </div>
    );

  if (!body || !content) return null;
  return createPortal(content, body);
}
